/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, Fragment, useEffect } from "react";
import TableFooter from "src/components/organisms/TableFooter";
import { SelectedRowKeys } from "src/components/organisms/TemplateView/fragments/TemplateTable/types";
import { EMPTY_OBJECT, noop } from "../../../../globalConsts";
import { TemplateId, WhereItems } from "../../../../globalTypes";
import { RowKey } from "../../../../requests/table/types";
import {
  EditRowWithColumns,
  TableRowValueDict,
} from "../../../../requests/tableRowActions/types";
import { useTemplateMetadataFromIdAUTOFETCH } from "../../../../store/templateMetadata/hooks/useSelectors";
import Loader from "../../../atoms/Loader";
import { DesktopOnly } from "../../../atoms/ViewportRestrictor";
import TableControls from "../../../organisms/AdministrationTable/TableControls";
import { getColumnOrderByColumnName } from "../../../organisms/TemplateView/fragments/TemplateTable/Container/utils";
import RelationTable from "./RelationTable";
import useRelationQueryObject from "./useRelationQueryObject";
import useRelationTableData from "./useRelationTableData";

interface ContainerProps {
  templateId: TemplateId;
  searchTextValue: string;
  onSelectCallback: (value: EditRowWithColumns) => void;
  rowKey?: RowKey;
  filter?: WhereItems;
  rowValues?: TableRowValueDict;
  detailTemplateId?: TemplateId | null;
  keysOfSelectedRows?: SelectedRowKeys;
}

const Container: FC<ContainerProps> = ({
  rowKey,
  templateId,
  onSelectCallback,
  filter = [],
  rowValues,
  detailTemplateId,
  keysOfSelectedRows,
  searchTextValue,
}) => {
  const {
    columnFormat,
    columnFormatMobile,
    mobileRowLayout,
    columnOrder,
    defaultSearchQuery,
  } = useTemplateMetadataFromIdAUTOFETCH(templateId) ?? {};

  const { sor, onOrder } = useRelationQueryObject(
    filter,
    rowKey,
    defaultSearchQuery
  );

  const {
    tableData,
    isLoading,
    loadMoreMobile,
    isLoadingMoreMobile,
    rowsPerPage,
    currentPage,
    searchText,
    setCurrentPage,
    setRowsPerPage,
    updateFulltext,
  } = useRelationTableData(
    templateId,
    sor,
    searchTextValue,
    rowValues,
    detailTemplateId
  );

  const columnOrderByColumnName = getColumnOrderByColumnName(
    columnOrder,
    tableData.columns
  );

  return (
    <Fragment>
      <TableControls
        allowRowInsertion={false}
        toggleRowInsertionModal={noop}
        onChangeOfRowsPerPage={setRowsPerPage}
        rowsPerPage={rowsPerPage}
        fulltextKeyword={searchText}
        onFulltextChange={updateFulltext}
        toggleAdvancedSearchUI={null}
        focusFullTextSearch
        moreBtnClickCallback={noop}
      />
      {isLoading ? (
        <Loader size={45} />
      ) : (
        <Fragment>
          <RelationTable
            rows={tableData.rows}
            columns={tableData.columns}
            onSelectCallback={onSelectCallback}
            columnFormatDict={columnFormat ?? EMPTY_OBJECT}
            columnFormatMobileDict={columnFormatMobile ?? EMPTY_OBJECT}
            positionData={mobileRowLayout}
            onOrder={onOrder}
            order={sor.order}
            isLoadingMoreMobile={isLoadingMoreMobile}
            loadMoreMobile={loadMoreMobile}
            totalRowCount={tableData.count}
            columnOrderByColumnName={columnOrderByColumnName}
            keysOfSelectedRows={keysOfSelectedRows}
          />
          <DesktopOnly>
            <TableFooter
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalRowCount={tableData.count}
              onPageChange={setCurrentPage}
            />
          </DesktopOnly>
        </Fragment>
      )}
    </Fragment>
  );
};

export default Container;
